import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarDensitySelector, GridToolbarQuickFilter } from '@mui/x-data-grid';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

// Search box + column visibility + density, all built into MUI's DataGrid
const CustomToolbar = () => (
    <GridToolbarContainer className="p-3 flex flex-wrap gap-2 items-center justify-between border-b">
        <div className="flex gap-1">
            <GridToolbarColumnsButton />
            <GridToolbarDensitySelector />
        </div>
        <GridToolbarQuickFilter placeholder="Search trash..." />
    </GridToolbarContainer>
);

const TrashProductTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectionModel, setSelectionModel] = useState([]);
    const [pageSize, setPageSize] = useState(25);

    const [categoryFilter, setCategoryFilter] = useState("");
    const [brandFilter, setBrandFilter] = useState("");

    const fetchTrash = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get('/api/v1/admin/products?status=trash');
            setProducts(data.products);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load trash", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTrash();
        const fetchCategories = async () => {
            try {
                const { data } = await axios.get('/api/v1/categories');
                const flatten = (cats, depth = 0) => cats.flatMap((c) => [
                    { id: c._id, name: `${'— '.repeat(depth)}${c.name}` },
                    ...(c.children ? flatten(c.children, depth + 1) : []),
                ]);
                setCategories(flatten(data.categories));
            } catch (error) {
                console.error('Failed to load categories', error);
            }
        };
        fetchCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const brandOptions = useMemo(() => {
        const names = products.map((p) => p.brand?.name).filter(Boolean);
        return [...new Set(names)].sort();
    }, [products]);

    const restoreHandler = async (id) => {
        try {
            await axios.put(`/api/v1/admin/product/${id}/restore`);
            enqueueSnackbar("Product restored", { variant: "success" });
            setProducts((prev) => prev.filter((p) => p._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to restore product", { variant: "error" });
        }
    };

    const permanentDeleteHandler = async (id) => {
        if (!window.confirm("Permanently delete this product? This cannot be undone.")) return;
        try {
            await axios.delete(`/api/v1/admin/product/${id}`);
            enqueueSnackbar("Product permanently deleted", { variant: "success" });
            setProducts((prev) => prev.filter((p) => p._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to delete product", { variant: "error" });
        }
    };

    const bulkRestoreHandler = async () => {
        try {
            await Promise.all(selectionModel.map((id) => axios.put(`/api/v1/admin/product/${id}/restore`)));
            enqueueSnackbar(`${selectionModel.length} product(s) restored`, { variant: "success" });
            setProducts((prev) => prev.filter((p) => !selectionModel.includes(p._id)));
            setSelectionModel([]);
        } catch (error) {
            enqueueSnackbar("Some products failed to restore", { variant: "error" });
        }
    };

    const bulkPermanentDeleteHandler = async () => {
        if (!window.confirm(`Permanently delete ${selectionModel.length} product(s)? This cannot be undone.`)) return;
        try {
            await Promise.all(selectionModel.map((id) => axios.delete(`/api/v1/admin/product/${id}`)));
            enqueueSnackbar(`${selectionModel.length} product(s) permanently deleted`, { variant: "success" });
            setProducts((prev) => prev.filter((p) => !selectionModel.includes(p._id)));
            setSelectionModel([]);
        } catch (error) {
            enqueueSnackbar("Some products failed to delete", { variant: "error" });
        }
    };

    const filteredProducts = useMemo(() => {
        return products.filter((p) => {
            if (categoryFilter && !(p.categories || []).some((c) => (c._id || c) === categoryFilter)) return false;
            if (brandFilter && p.brand?.name !== brandFilter) return false;
            return true;
        });
    }, [products, categoryFilter, brandFilter]);

    const activeFilters = [
        categoryFilter && { key: "category", label: `Category: ${categories.find((c) => c.id === categoryFilter)?.name || ""}`, clear: () => setCategoryFilter("") },
        brandFilter && { key: "brand", label: `Brand: ${brandFilter}`, clear: () => setBrandFilter("") },
    ].filter(Boolean);

    const columns = [
        {
            field: "name",
            headerName: "Name",
            minWidth: 220,
            flex: 1,
            renderCell: (params) => (
                <div className="flex items-center gap-3">
                    <img draggable="false" src={params.row.image} alt={params.row.name} className="w-10 h-10 rounded object-cover border opacity-70" />
                    <span>{params.row.name}</span>
                </div>
            ),
        },
        {
            field: "category",
            headerName: "Category",
            minWidth: 160,
            flex: 0.6,
        },
        {
            field: "brand",
            headerName: "Brand",
            minWidth: 120,
            flex: 0.4,
        },
        {
            field: "stock",
            headerName: "Stock",
            type: "number",
            minWidth: 80,
            flex: 0.3,
        },
        {
            field: "price",
            headerName: "Sale Price",
            type: "number",
            minWidth: 110,
            flex: 0.3,
            renderCell: (params) => <span>HK${params.row.price?.toLocaleString()}</span>,
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 110,
            flex: 0.3,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <div className="flex items-center gap-1">
                    <button onClick={() => restoreHandler(params.row.id)} className="text-primary-green hover:bg-green-50 p-1.5 rounded-full" title="Restore"><RestoreIcon sx={{ fontSize: "20px" }} /></button>
                    <button onClick={() => permanentDeleteHandler(params.row.id)} className="text-red-600 hover:bg-red-50 p-1.5 rounded-full" title="Delete Permanently"><DeleteForeverIcon sx={{ fontSize: "20px" }} /></button>
                </div>
            ),
        },
    ];

    const rows = filteredProducts.map((item) => ({
        id: item._id,
        name: item.name,
        image: item.images && item.images.length > 0 ? item.images[0].url : "https://placehold.co/100x100?text=No+Image",
        category: item.categories && item.categories.length > 0 ? item.categories.map((c) => c.name).join(", ") : "—",
        brand: item.brand?.name || "—",
        stock: item.stock,
        price: item.price,
    }));

    return (
        <>
            <MetaData title="Admin: Trash | 1313 Mart" />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <div>
                    <Link to="/admin/products" className="text-sm text-primary-blue hover:underline">&larr; Back to Products</Link>
                    <h1 className="text-lg font-medium uppercase mt-1 flex items-center gap-2"><RestorePageIcon sx={{ fontSize: "22px" }} /> Trash</h1>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full overflow-hidden">

                {/* Filter bar */}
                <div className="flex flex-wrap items-center gap-3 p-4 border-b">
                    <TextField label="Category" select size="small" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} sx={{ minWidth: 180 }}>
                        <MenuItem value="">All Categories</MenuItem>
                        {categories.map((c) => <MenuItem value={c.id} key={c.id}>{c.name}</MenuItem>)}
                    </TextField>
                    <Autocomplete
                        options={brandOptions}
                        value={brandFilter || null}
                        onChange={(e, newValue) => setBrandFilter(newValue || "")}
                        size="small"
                        sx={{ minWidth: 200 }}
                        renderInput={(params) => <TextField {...params} label="Brand" placeholder="Search brands..." />}
                    />

                    {selectionModel.length > 0 && (
                        <div className="ml-auto flex gap-2">
                            <button onClick={bulkRestoreHandler} className="flex items-center gap-1.5 text-sm bg-green-50 text-primary-green border border-green-200 px-3 py-2 rounded hover:bg-green-100">
                                <RestoreIcon sx={{ fontSize: "18px" }} /> Restore {selectionModel.length}
                            </button>
                            <button onClick={bulkPermanentDeleteHandler} className="flex items-center gap-1.5 text-sm bg-red-50 text-red-600 border border-red-200 px-3 py-2 rounded hover:bg-red-100">
                                <DeleteForeverIcon sx={{ fontSize: "18px" }} /> Delete {selectionModel.length} Permanently
                            </button>
                        </div>
                    )}
                </div>

                {/* Active filter chips */}
                {activeFilters.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b bg-gray-50">
                        <span className="text-xs text-gray-500">Active filters:</span>
                        {activeFilters.map((f) => (
                            <Chip key={f.key} label={f.label} size="small" onDelete={f.clear} />
                        ))}
                        <button
                            onClick={() => { setCategoryFilter(""); setBrandFilter(""); }}
                            className="text-xs text-primary-blue hover:underline ml-1"
                        >
                            Clear All
                        </button>
                    </div>
                )}

                <div style={{ height: 600 }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        checkboxSelection
                        disableSelectIconOnClick
                        rowHeight={64}
                        pageSize={pageSize}
                        onPageSizeChange={(newSize) => setPageSize(newSize)}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        selectionModel={selectionModel}
                        onSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
                        components={{ Toolbar: CustomToolbar }}
                        sx={{
                            border: 0,
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f9fafb', fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280' },
                            '& .MuiDataGrid-cell': { fontSize: '0.875rem' },
                            '& .MuiDataGrid-row:hover': { backgroundColor: '#f9fafb' },
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default TrashProductTable;
